import { unauthorizedCountWithLocalStorage } from '@/lib/unauthorizedCountWithLocalStorage'
import { useLocalStorageClear } from '@/lib/useLocalStorageClear'
import { useTokenRefresh } from '@/lib/useTokenRefresh'
import { useCampaignHeaders } from './useCampaignHeaders'
import { useCampaignSlug } from './useCampaignSlug'

type GetResponse<T> = (url?: string) => Promise<T>
type PostResponse<T> = (url?: string, body?: any) => Promise<T | null>
type DeleteResponse<T> = (url?: string) => Promise<T>

export const useFetchGet = <T>({
  useUrl,
  isTokenRefresh = true,
}: { useUrl?: string; isTokenRefresh?: boolean }): { getWithoutAuth: GetResponse<T>; getWithAuth: GetResponse<T> } => {
  const { gen, LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY } = useCampaignSlug()
  const { headers, headersWithAuthorization } = useCampaignHeaders()
  const { refreshToken } = useTokenRefresh()
  const clear = useLocalStorageClear()

  return {
    getWithoutAuth: async <T>(url?: string) => {
      const fixedUrl = url || useUrl
      if (!fixedUrl) {
        throw new Error('URL is required for getWithoutAuth')
      }
      try {
        const res = await fetch(fixedUrl, {
          method: 'GET',
          headers,
        })

        if (res.status === 400 || res.status === 401) {
          return
        }

        const currentUrl = window.location.href
        if (!currentUrl.includes('/500')) {
          if (res.status === 500) {
            location.href = gen('/500')
            return
          }
        }

        return (await res.json()) as T
      } catch (error) {
        const currentUrl = window.location.href
        if (!(currentUrl.includes('/500') || currentUrl.includes('/'))) {
          location.href = gen('/500')
          return
        }
      }
    },
    getWithAuth: async <T>(url?: string) => {
      const fixedUrl = url || useUrl
      if (!fixedUrl) {
        throw new Error('URL is required for getWithoutAuth')
      }
      try {
        const res = await fetch(url, {
          method: 'GET',
          headers: headersWithAuthorization,
        })

        const result = await res.json()

        if (res.status === 200) {
          unauthorizedCountWithLocalStorage({ initial: 1, key: LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY })
        }
        if (res.status === 400) {
          return
        }
        if (res.status === 401 && isTokenRefresh) {
          const unAuthorizedCount = unauthorizedCountWithLocalStorage({ key: LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY })
          if (unAuthorizedCount >= 20) {
            location.href = gen('/')
            clear()
          } else {
            await refreshToken()
          }
          return
        }

        const currentUrl = window.location.href
        if (!currentUrl.includes('/500')) {
          if (res.status === 500) {
            location.href = gen('/500')
            return
          }
        }

        return result as T
      } catch (error) {
        const currentUrl = window.location.href
        if (!(currentUrl.includes('/500') || currentUrl.includes('/'))) {
          location.href = gen('/500')
          return
        }
      }
    },
  }
}

export const useFetchPost = <T>({
  useUrl,
  isTokenRefresh = true,
}: { useUrl?: string; isTokenRefresh?: boolean }): {
  postWithoutAuth: PostResponse<T>
  postWithAuth: PostResponse<T>
} => {
  const { gen } = useCampaignSlug()
  const { headers, headersWithAuthorization } = useCampaignHeaders()
  const { refreshToken } = useTokenRefresh()
  const clear = useLocalStorageClear()

  return {
    postWithoutAuth: async <T>(url?: string, body?: any) => {
      const fixedUrl = url || useUrl
      if (!fixedUrl) {
        throw new Error('URL is required for getWithoutAuth')
      }
      try {
        const res = await fetch(fixedUrl, {
          method: 'POST',
          headers,
        })

        const result = await res.json()

        if (res.status === 400) {
          return
        }
        if (res.status === 401) {
          refreshToken()
          return
        }

        const currentUrl = window.location.href
        if (!currentUrl.includes('/500')) {
          if (res.status === 500) {
            return
          }
        }

        return result
      } catch (error) {
        console.log(error)
      }
    },
    postWithAuth: async <T>(url?: string, body?: any) => {
      const fixedUrl = url || useUrl
      if (!fixedUrl) {
        throw new Error('URL is required for getWithoutAuth')
      }
      try {
        const res = await fetch(fixedUrl, {
          method: 'POST',
          headers: headersWithAuthorization,
          body: JSON.stringify(body ? body : {}),
        })

        const result = await res.json()

        if (res.status === 401) {
          refreshToken()
          return
        }

        const currentUrl = window.location.href
        if (!currentUrl.includes('/500')) {
          if (res.status === 500) {
            location.href = gen('/500')
            return
          }
        }

        return result
      } catch (error) {
        console.log({ error })
      }
    },
  }
}

export const useFetchDelete = <T>({
  useUrl,
  isTokenRefresh = true,
}: { useUrl?: string; isTokenRefresh?: boolean }): {
  deleteWithAuth: DeleteResponse<T>
} => {
  const { gen } = useCampaignSlug()
  const { headers, headersWithAuthorization } = useCampaignHeaders()
  const { refreshToken } = useTokenRefresh()
  const clear = useLocalStorageClear()

  return {
    deleteWithAuth: async <T>(url?: string) => {
      const fixedUrl = url || useUrl
      if (!fixedUrl) {
        throw new Error('URL is required for getWithoutAuth')
      }
      try {
        const res = await fetch(`${url}`, {
          method: 'DELETE',
          headers: headersWithAuthorization,
        })

        const result = await res.json()

        if (res.status === 401) {
          refreshToken()
          return
        }

        const currentUrl = window.location.href
        if (!currentUrl.includes('/500')) {
          if (res.status === 500) {
            location.href = gen('/500')
            return
          }
        }

        return result
      } catch (error) {
        console.log(error)
      }
    },
  }
}
